import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import FilterListIcon from '@mui/icons-material/FilterList';
import EventNoteIcon from '@mui/icons-material/EventNote';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import {
  PadelBooking,
  useGetAdminPadelBookingsQuery,
} from '../../redux/residentPortalApi';
import { formatCurrency } from '../../utils/currencyUtil';

const ALL_SLOTS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

const formatSlot = (hour: number) => {
  const pad = (h: number) => (h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`);
  return `${pad(hour)}–${pad(hour + 1)}`;
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; color: 'success' | 'warning' | 'error'; icon: React.ReactElement }> = {
    confirmed: { label: 'Confirmed', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    pending: { label: 'Pending', color: 'warning', icon: <HourglassEmptyIcon fontSize="small" /> },
    cancelled: { label: 'Cancelled', color: 'error', icon: <CancelIcon fontSize="small" /> },
  };
  const s = map[status] ?? { label: status, color: 'default' as any, icon: <></> };
  return <Chip label={s.label} color={s.color} size="small" icon={s.icon} />;
};

const timetableHours = ALL_SLOTS;

const PadelBookingsAdmin: React.FC = () => {
  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [view, setView] = useState<'table' | 'timetable'>('timetable');

  const dateParam = filterDate ? format(filterDate, 'yyyy-MM-dd') : undefined;

  const { data: bookings, isFetching, refetch } = useGetAdminPadelBookingsQuery({
    date: dateParam,
    status: filterStatus || undefined,
  });

  const clearFilters = () => {
    setFilterDate(null);
    setFilterStatus('');
  };

  // ---- Timetable helpers ----
  // Group confirmed bookings by date for a calendar view
  const confirmedByDate = React.useMemo(() => {
    if (!bookings) return {};
    const map: Record<string, PadelBooking[]> = {};
    bookings.forEach(b => {
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    });
    return map;
  }, [bookings]);

  const timetableDates = React.useMemo(
    () => Object.keys(confirmedByDate).sort(),
    [confirmedByDate]
  );

  const findBookingForSlot = (date: string, hour: number): PadelBooking | undefined =>
    confirmedByDate[date]?.find(b => b.slots.includes(hour));

  const totalConfirmed = bookings?.filter(b => b.status === 'confirmed').length ?? 0;
  const totalPending = bookings?.filter(b => b.status === 'pending').length ?? 0;
  const totalRevenue = bookings
    ?.filter(b => b.status === 'confirmed')
    .reduce((sum, b) => sum + (b.totalAmount / 100), 0) ?? 0;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SportsTennisIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#2c3e50">
            Padel Court Bookings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage all padel court reservations
          </Typography>
        </Box>
        <Box flexGrow={1} />
        <Tooltip title="Refresh">
          <Button
            variant="outlined"
            size="small"
            startIcon={isFetching ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Tooltip>
      </Stack>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Bookings', value: bookings?.length ?? 0, color: '#1a5276', bg: '#eaf3fa' },
          { label: 'Confirmed', value: totalConfirmed, color: '#27ae60', bg: '#eafaf1' },
          { label: 'Pending', value: totalPending, color: '#e67e22', bg: '#fef9ee' },
          { label: 'Revenue (confirmed)', value: `$${totalRevenue.toFixed(2)}`, color: '#8e44ad', bg: '#f5eef8' },
        ].map(stat => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: stat.bg,
                border: `1px solid ${stat.color}22`,
                textAlign: 'center',
              }}
            >
              <Typography variant="h4" fontWeight={800} color={stat.color}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e8ecef', mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            Filters
          </Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Filter by date"
                value={filterDate}
                onChange={setFilterDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={e => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: 'none', color: '#95a5a6' }}
            >
              Clear filters
            </Button>
          </Grid>
          <Grid item xs="auto" ml="auto">
            <Stack direction="row" spacing={1}>
              <Button
                variant={view === 'timetable' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setView('timetable')}
                startIcon={<EventNoteIcon />}
                sx={{ textTransform: 'none' }}
              >
                Timetable
              </Button>
              <Button
                variant={view === 'table' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setView('table')}
                sx={{ textTransform: 'none' }}
              >
                Table
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Content */}
      {isFetching ? (
        <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e8ecef' }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height={56} sx={{ mb: 1 }} />)}
        </Paper>
      ) : !bookings?.length ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: '12px', border: '1px solid #e8ecef', textAlign: 'center' }}>
          <SportsTennisIcon sx={{ fontSize: 64, color: '#dce3ea', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No bookings found</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Try adjusting your filters or check back later.
          </Typography>
        </Paper>
      ) : view === 'table' ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e8ecef' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Slots</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...bookings].sort((a, b) => b.date.localeCompare(a.date)).map(booking => (
                <TableRow key={booking.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{booking.date}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(booking as any).property
                        ? `Unit ${(booking as any).property.unitNumber ?? ''} – ${(booking as any).property.development ?? ''}`
                        : booking.propertyId ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {[...booking.slots].sort((a, b) => a - b).map(s => (
                        <Chip key={s} label={formatSlot(s)} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      ${(booking.totalAmount / 100).toFixed(2)} {booking.currency.toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={booking.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {booking.paymentReference}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Timetable view */
        timetableDates.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, borderRadius: '12px', border: '1px solid #e8ecef', textAlign: 'center' }}>
            <SportsTennisIcon sx={{ fontSize: 64, color: '#dce3ea', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No confirmed bookings</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              There are no confirmed bookings matching your current filters.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e8ecef', overflow: 'hidden' }}>
            {/* Sticky header row */}
            <Box sx={{ overflowX: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `120px repeat(${timetableHours.length}, 1fr)`,
                  minWidth: 900,
                }}
              >
                {/* Header */}
                <Box sx={{ backgroundColor: '#1a5276', p: 1.5, position: 'sticky', left: 0 }}>
                  <Typography variant="caption" fontWeight={700} color="#fff">DATE</Typography>
                </Box>
                {timetableHours.map(h => (
                  <Box key={h} sx={{ backgroundColor: '#1a5276', p: 1.5, textAlign: 'center', borderLeft: '1px solid #2e86c122' }}>
                    <Typography variant="caption" fontWeight={700} color="#fff">
                      {h}:00
                    </Typography>
                  </Box>
                ))}

                {/* Rows */}
                {timetableDates.map((date, di) => (
                  <React.Fragment key={date}>
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: di % 2 === 0 ? '#f8f9fa' : '#fff',
                        borderTop: '1px solid #e8ecef',
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} color="#2c3e50">
                        {date}
                      </Typography>
                    </Box>
                    {timetableHours.map(hour => {
                      const booking = findBookingForSlot(date, hour);
                      return (
                        <Tooltip
                          key={hour}
                          title={
                            booking
                              ? `Property: ${(booking as any).property?.unitNumber ?? booking.propertyId} · $${(booking.totalAmount / 100).toFixed(2)}`
                              : 'Available'
                          }
                          arrow
                        >
                          <Box
                            sx={{
                              borderTop: '1px solid #e8ecef',
                              borderLeft: '1px solid #e8ecef',
                              backgroundColor: booking
                                ? booking.status === 'confirmed'
                                  ? '#27ae60'
                                  : booking.status === 'pending'
                                  ? '#f39c12'
                                  : '#e74c3c'
                                : di % 2 === 0 ? '#f8f9fa' : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 42,
                              cursor: booking ? 'pointer' : 'default',
                              transition: 'opacity 0.15s',
                              '&:hover': booking ? { opacity: 0.8 } : {},
                            }}
                          >
                            {booking && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#fff',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  textAlign: 'center',
                                  px: 0.5,
                                  lineHeight: 1.2,
                                }}
                              >
                                {(booking as any).property?.unitNumber
                                  ? `U${(booking as any).property.unitNumber}`
                                  : '✓'}
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            {/* Timetable legend */}
            <Box sx={{ p: 2, borderTop: '1px solid #e8ecef', backgroundColor: '#f8f9fa' }}>
              <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary" fontWeight={600}></Typography>
                {[
                  { color: '#27ae60', label: 'Confirmed' },
                  { color: '#f39c12', label: 'Pending' },
                  { color: '#e74c3c', label: 'Cancelled' },
                  { color: '#e8ecef', label: 'Available' },
                ].map(l => (
                  <Stack direction="row" spacing={0.8} alignItems="center" key={l.label}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: l.color, border: '1px solid #ccc' }} />
                    <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Paper>
        )
      )}
    </Box>
  );
};

export default PadelBookingsAdmin;
